import React from 'react'
import type { QuizAttempt } from '@/types/quiz'

interface QuizResultProps {
  attempt: QuizAttempt
  maxAttempts: number
  passingScore: number
  hasEssay?: boolean
  hasMC?: boolean
  onRetry?: () => void
  onClose: () => void
}

export const QuizResult: React.FC<QuizResultProps> = ({
  attempt,
  maxAttempts,
  passingScore,
  hasEssay = false,
  hasMC = true,
  onRetry,
  onClose
}) => {
  const calculateTimeTaken = (): string => {
    if (!attempt.completedAt) {
      return ''
    }

    const start = new Date(attempt.startedAt).getTime()
    const end = new Date(attempt.completedAt).getTime()
    const diffSeconds = Math.floor((end - start) / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)

    if (diffMinutes < 1) {
      return `${diffSeconds} seconds`
    }

    return `${diffMinutes} minutes`
  }

  const timeTaken = calculateTimeTaken()
  // Allow retry if: not passed AND attempts remaining AND (not hasEssay OR already graded)
  const canRetry = onRetry && attempt.attemptNumber < maxAttempts && !attempt.isPassed && 
    (attempt.status === 'graded' || !hasEssay)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-surface-card rounded-lg p-8 max-w-2xl w-full border border-gray-200 dark:border-white/10">
        {/* Header with Status Badge */}
        <div className="flex justify-between items-start mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Hasil Quiz</h2>
          <span
            className={`px-4 py-2 text-sm font-bold rounded-full uppercase tracking-wider ${
              attempt.status === 'graded'
                ? attempt.isPassed
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
                : hasEssay
                  ? 'bg-amber-500/20 text-amber-400'
                  : attempt.isPassed
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
            }`}
          >
            {attempt.status === 'graded'
              ? attempt.isPassed ? 'Lulus' : 'Belum Lulus'
              : hasEssay ? 'Menunggu Review' : attempt.isPassed ? 'Lulus' : 'Belum Lulus'}
          </span>
        </div>

        {/* Essay Pending Review Banner */}
        {hasEssay && attempt.status !== 'graded' && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-amber-400 text-[24px] mt-0.5">rate_review</span>
              <div>
                <p className="text-sm font-bold text-amber-400 mb-1">
                  {hasMC ? 'Jawaban essay menunggu review' : 'Menunggu koreksi instruktur'}
                </p>
                <p className="text-xs text-gray-400">
                  {hasMC 
                    ? 'Soal pilihan ganda sudah dinilai otomatis. Jawaban essay Anda akan direview oleh instruktur untuk nilai akhir.'
                    : 'Seluruh jawaban Anda adalah essay. Instruktur akan memeriksa jawaban Anda secara manual.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Instructor Feedback */}
        {attempt.status === 'graded' && attempt.feedback && (
          <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-blue-400 text-[24px] mt-0.5">comment</span>
              <div>
                <p className="text-sm font-bold text-blue-400 mb-1">Catatan Evaluasi Instruktur</p>
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{attempt.feedback}</p>
              </div>
            </div>
          </div>
        )}

        {/* Score Display */}
        <div className="text-center mb-8">
          {attempt.status === 'graded' ? (
            <>
              <div className={`text-5xl sm:text-7xl font-bold mb-3 ${
                attempt.isPassed ? 'text-green-400' : 'text-red-400'
              }`}>
                {attempt.score}%
              </div>
              <p className="text-lg text-gray-500 dark:text-text-muted mb-2">
                {attempt.isPassed
                  ? 'Selamat! Anda lulus quiz ini.'
                  : `Anda butuh ${passingScore}% untuk lulus.`}
              </p>
            </>
          ) : hasEssay && (!hasMC || attempt.status === 'needs_review') ? (
            <div className="flex flex-col items-center py-4">
              <div className="size-20 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-amber-400 text-4xl">pending_actions</span>
              </div>
              <p className="text-xl font-bold text-white">Nilai Sedang Diproses</p>
              <p className="text-sm text-gray-400 mt-1">Anda akan mendapatkan notifikasi setelah review selesai.</p>
            </div>
          ) : (
            <>
              <div className={`text-5xl sm:text-7xl font-bold mb-3 ${
                attempt.isPassed ? 'text-green-400' : 'text-red-400'
              }`}>
                {attempt.score}%
              </div>
              <p className="text-lg text-gray-500 dark:text-text-muted mb-2">
                {attempt.isPassed
                  ? 'Selamat! Anda lulus quiz ini.'
                  : `Anda butuh ${passingScore}% untuk lulus.`}
              </p>
            </>
          )}
          
          {attempt.isPassed && (
            <div className="flex items-center justify-center gap-2 text-green-400 mt-2">
              <span className="material-symbols-outlined text-3xl">celebration</span>
              <span className="font-semibold">Kerja bagus!</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <div className="bg-gray-100 dark:bg-white/5 p-5 rounded-lg border border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-[20px]">counter_1</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-text-muted uppercase tracking-wider">Attempt</p>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {attempt.attemptNumber} of {maxAttempts}
            </p>
          </div>

          {timeTaken && (
            <div className="bg-gray-100 dark:bg-white/5 p-5 rounded-lg border border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="size-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-[20px]">schedule</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-text-muted uppercase tracking-wider">Time Taken</p>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{timeTaken}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white font-semibold hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
            <span>Close</span>
          </button>

          {canRetry && (
            <button
              onClick={onRetry}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary text-white font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-[20px]">refresh</span>
              <span>Try Again</span>
            </button>
          )}
        </div>

        {/* Retry Info */}
        {!attempt.isPassed && !canRetry && attempt.attemptNumber >= maxAttempts && (
          <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-red-400 text-[20px]">info</span>
              <p className="text-sm text-red-400">
                You've used all {maxAttempts} attempts. Contact your instructor for help.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
